import { Fragment, type ReactElement, useMemo, useState } from 'react'
import { isDefined, isEmpty, type PermissionDefinition, type PermissionList, type PermissionType } from '@terros/common'
import { Badge, Box, Group, ScrollArea, Stack, Table, Text, TextInput } from '@mantine/core'
import {
  type GroupedPermissionDefinitions,
  type PermissionDefinitionGroup,
  type TranslateFunction,
  usePermissionComparisons,
  useTranslation,
} from '@front/model'
import { filterPermissionsRecord, MaterialSymbolIcon, useDisplayPermissionsRecord } from '@components'
import { PermissionComparisonRow } from './PermissionComparisonRow'
import { ENTITY_COLUMN_WIDTH, PERMISSION_COLUMN_WIDTH } from './permissionComparisonConstants'
import { type CompareEntity, isPermissionDifferentFromControl } from './compareUtil'
import { ComparePermissionHeader } from './ComparePermissionHeader'

type PermissionComparisonRowData = {
  category: string
  definition: PermissionDefinition
  permissionType: PermissionType
}

export const PermissionComparisonTable = ({
  selectedEntities,
}: {
  selectedEntities: CompareEntity[]
}): ReactElement => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState('')
  const [isDifferencesOpen, setIsDifferencesOpen] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const results = usePermissionComparisons(selectedEntities.map((entity) => entity.id))

  const permissionList = useMemo<PermissionList>(
    () => Object.assign({}, ...results.map((result) => result.data?.list ?? {})),
    [results]
  )
  const defaultRecord = useDisplayPermissionsRecord(permissionList)
  const filteredRecord = useMemo(() => filterPermissionsRecord(defaultRecord, filter), [defaultRecord, filter])
  const permissionLists = useMemo(() => results.map((result) => result.data?.list), [results])
  const { differenceRows, matchingRecord } = useMemo(
    () => partitionPermissionRows(filteredRecord, permissionLists),
    [filteredRecord, permissionLists]
  )
  const hasAllPermissionLists = permissionLists.every(isDefined)
  const emptyDifferencesMessage = getEmptyDifferencesMessage(filter, selectedEntities.length, t)

  const toggleCategory = (category: string): void => {
    setSelectedCategory(category === selectedCategory ? null : category)
  }

  return (
    <Stack miw={0} w='100%'>
      <TextInput
        leftSection={<MaterialSymbolIcon icon='search' />}
        placeholder={t('Search permissions')}
        value={filter}
        onChange={(event) => setFilter(event.currentTarget.value)}
      />
      <Box className='border-default-border min-w-0 overflow-hidden rounded-lg border' w='100%'>
        <ScrollArea type='auto' w='100%'>
          <Table
            horizontalSpacing='lg'
            layout='fixed'
            verticalSpacing='md'
            withColumnBorders
            style={{ minWidth: PERMISSION_COLUMN_WIDTH + selectedEntities.length * ENTITY_COLUMN_WIDTH }}
          >
            <ComparePermissionHeader entities={selectedEntities} results={results} />
            <Table.Tbody>
              <Table.Tr>
                <Table.Th className='bg-brand-light' colSpan={selectedEntities.length + 1} p={0}>
                  <button
                    aria-expanded={isDifferencesOpen}
                    className='w-full cursor-pointer px-4 py-3 text-left'
                    type='button'
                    onClick={() => setIsDifferencesOpen((isOpen) => !isOpen)}
                  >
                    <Group justify='space-between' wrap='nowrap'>
                      <Group gap='xs'>
                        <Text fw='bold'>{t('Differences')}</Text>
                        <Badge color='gray' variant='light'>
                          {differenceRows.length}
                        </Badge>
                      </Group>
                      <MaterialSymbolIcon icon={isDifferencesOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
                    </Group>
                  </button>
                </Table.Th>
              </Table.Tr>
              {isDifferencesOpen &&
                differenceRows.map(({ category, definition, permissionType }) => (
                  <PermissionComparisonRow
                    key={permissionType}
                    category={category}
                    definition={definition}
                    permissionType={permissionType}
                    results={results}
                    selectedEntities={selectedEntities}
                  />
                ))}
              {isDifferencesOpen && hasAllPermissionLists && isEmpty(differenceRows) && (
                <Table.Tr>
                  <Table.Td colSpan={selectedEntities.length + 1}>
                    <Text c='dimmed' ta='center'>
                      {emptyDifferencesMessage}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {hasAllPermissionLists &&
                Array.from(matchingRecord.entries()).map(([category, definitions]) => (
                  <Fragment key={category}>
                    <Table.Tr>
                      <Table.Th className='bg-brand-light' colSpan={selectedEntities.length + 1} p={0}>
                        <button
                          aria-expanded={selectedCategory === category}
                          className='w-full cursor-pointer px-4 py-3 text-left'
                          type='button'
                          onClick={() => toggleCategory(category)}
                        >
                          <Group justify='space-between' wrap='nowrap'>
                            <Group gap='xs'>
                              <Text fw='bold'>{category}</Text>
                              <Badge color='gray' variant='light'>
                                {definitions.size}
                              </Badge>
                            </Group>
                            <MaterialSymbolIcon
                              icon={selectedCategory === category ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                            />
                          </Group>
                        </button>
                      </Table.Th>
                    </Table.Tr>
                    {selectedCategory === category &&
                      Array.from(definitions.entries()).map(([permissionType, definition]) => (
                        <PermissionComparisonRow
                          key={permissionType}
                          definition={definition}
                          permissionType={permissionType}
                          results={results}
                          selectedEntities={selectedEntities}
                        />
                      ))}
                  </Fragment>
                ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Box>
    </Stack>
  )
}

function partitionPermissionRows(
  record: GroupedPermissionDefinitions,
  permissionLists: (PermissionList | undefined)[]
): { differenceRows: PermissionComparisonRowData[]; matchingRecord: GroupedPermissionDefinitions } {
  const differenceRows: PermissionComparisonRowData[] = []
  const matchingRecord: GroupedPermissionDefinitions = new Map<string, PermissionDefinitionGroup>()
  for (const [category, definitions] of record) {
    const matchingDefinitions: PermissionDefinitionGroup = new Map()
    for (const [permissionType, definition] of definitions) {
      if (isPermissionDifferentFromControl(permissionLists, permissionType)) {
        differenceRows.push({ category, definition, permissionType })
      } else {
        matchingDefinitions.set(permissionType, definition)
      }
    }
    if (matchingDefinitions.size > 0) matchingRecord.set(category, matchingDefinitions)
  }
  return { differenceRows, matchingRecord }
}

function getEmptyDifferencesMessage(filter: string, selectedEntityCount: number, t: TranslateFunction): string {
  if (filter) return t('No permission differences match your search.')
  if (selectedEntityCount === 1) return t('Add another entity to compare against the control.')
  return t('These selections have matching permissions.')
}
